/**
 * User Preferences Store (Zustand)
 *
 * Persistent user preferences management for:
 * - Language
 * - Date/time format
 * - Currency
 * - Pagination defaults
 * - Notification preferences
 * - Display preferences
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import i18n from '@/locales';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Supported languages
 */
export type Language = 'en' | 'sw';

/**
 * Date format options
 */
export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY';

/**
 * Time format options
 */
export type TimeFormat = '12h' | '24h';

/**
 * Currency options
 */
export type Currency = 'TZS' | 'USD' | 'EUR' | 'GBP';

/**
 * Number format options
 */
export type NumberFormat = 'comma' | 'space' | 'dot';

/**
 * Density options (spacing/padding)
 */
export type Density = 'compact' | 'comfortable' | 'spacious';

/**
 * Theme mode options
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  /** Enable browser notifications */
  enableBrowser: boolean;
  /** Enable email notifications */
  enableEmail: boolean;
  /** Enable push notifications */
  enablePush: boolean;
  /** Notify on policy updates */
  notifyPolicyUpdates: boolean;
  /** Notify on claim updates */
  notifyClaimUpdates: boolean;
  /** Notify on customer updates */
  notifyCustomerUpdates: boolean;
  /** Notify on system updates */
  notifySystemUpdates: boolean;
  /** Sound enabled */
  soundEnabled: boolean;
}

/**
 * Theme preferences
 */
export interface ThemePreferences {
  /** Color preset ID */
  colorPresetId: string;
  /** Theme mode (light/dark/system) */
  mode: ThemeMode;
  /** Font family ID */
  fontFamilyId: string;
}

/**
 * Display preferences
 */
export interface DisplayPreferences {
  /** Show tooltips */
  showTooltips: boolean;
  /** Show keyboard shortcuts */
  showKeyboardShortcuts: boolean;
  /** Animate transitions */
  animateTransitions: boolean;
  /** Density/spacing */
  density: Density;
  /** Show profile pictures */
  showProfilePictures: boolean;
  /** Show status badges */
  showStatusBadges: boolean;
}

/**
 * Pagination preferences
 */
export interface PaginationPreferences {
  /** Default page size */
  defaultPageSize: number;
  /** Show page size selector */
  showPageSizeSelector: boolean;
  /** Show total count */
  showTotalCount: boolean;
}

/**
 * Table preferences
 */
export interface TablePreferences {
  /** Striped rows */
  stripedRows: boolean;
  /** Hover highlight */
  hoverHighlight: boolean;
  /** Compact mode */
  compactMode: boolean;
  /** Show borders */
  showBorders: boolean;
  /** Sticky header */
  stickyHeader: boolean;
}

/**
 * User Preferences Store State
 */
export interface UserPreferencesState {
  // ===== LOCALE =====
  /** Current language */
  language: Language;
  /** Date format */
  dateFormat: DateFormat;
  /** Time format */
  timeFormat: TimeFormat;
  /** Currency */
  currency: Currency;
  /** Number format */
  numberFormat: NumberFormat;
  /** Timezone (IANA timezone identifier) */
  timezone: string;

  // ===== PREFERENCES =====
  /** Theme preferences */
  theme: ThemePreferences;
  /** Notification preferences */
  notifications: NotificationPreferences;
  /** Display preferences */
  display: DisplayPreferences;
  /** Pagination preferences */
  pagination: PaginationPreferences;
  /** Table preferences */
  table: TablePreferences;

  // ===== RECENTLY VIEWED =====
  /** Recently viewed items (max 10) */
  recentlyViewed: Array<{
    type: 'policy' | 'claim' | 'customer';
    id: string;
    title: string;
    timestamp: number;
  }>;

  // ===== FAVORITES =====
  /** Favorite items */
  favorites: Array<{
    type: 'policy' | 'claim' | 'customer' | 'report';
    id: string;
    title: string;
    timestamp: number;
  }>;

  // ===== DASHBOARD LAYOUT =====
  /** Dashboard widget layout */
  dashboardLayout: Array<{
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
    visible: boolean;
  }>;

  // ===== ACTIONS =====
  // Locale actions
  setLanguage: (language: Language) => void;
  setDateFormat: (format: DateFormat) => void;
  setTimeFormat: (format: TimeFormat) => void;
  setCurrency: (currency: Currency) => void;
  setNumberFormat: (format: NumberFormat) => void;
  setTimezone: (timezone: string) => void;

  // Theme preferences
  setThemePreferences: (prefs: Partial<ThemePreferences>) => void;
  setColorPreset: (presetId: string) => void;
  setThemeMode: (mode: ThemeMode) => void;
  setFontFamily: (fontId: string) => void;

  // Notification preferences
  setNotificationPreferences: (prefs: Partial<NotificationPreferences>) => void;
  toggleNotification: (key: keyof NotificationPreferences) => void;

  // Display preferences
  setDisplayPreferences: (prefs: Partial<DisplayPreferences>) => void;
  setDensity: (density: Density) => void;

  // Pagination preferences
  setPaginationPreferences: (prefs: Partial<PaginationPreferences>) => void;
  setDefaultPageSize: (size: number) => void;

  // Table preferences
  setTablePreferences: (prefs: Partial<TablePreferences>) => void;

  // Recently viewed
  addRecentlyViewed: (item: {
    type: 'policy' | 'claim' | 'customer';
    id: string;
    title: string;
  }) => void;
  clearRecentlyViewed: () => void;

  // Favorites
  addFavorite: (item: {
    type: 'policy' | 'claim' | 'customer' | 'report';
    id: string;
    title: string;
  }) => void;
  removeFavorite: (type: string, id: string) => void;
  isFavorite: (type: string, id: string) => boolean;
  clearFavorites: () => void;

  // Dashboard layout
  setDashboardLayout: (
    layout: Array<{ id: string; x: number; y: number; w: number; h: number; visible: boolean }>
  ) => void;
  toggleWidgetVisibility: (widgetId: string) => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const defaultThemePreferences: ThemePreferences = {
  colorPresetId: 'coral-sunrise',
  mode: 'system',
  fontFamilyId: 'inter',
};

const defaultNotificationPreferences: NotificationPreferences = {
  enableBrowser: true,
  enableEmail: true,
  enablePush: false,
  notifyPolicyUpdates: true,
  notifyClaimUpdates: true,
  notifyCustomerUpdates: false,
  notifySystemUpdates: true,
  soundEnabled: true,
};

const defaultDisplayPreferences: DisplayPreferences = {
  showTooltips: true,
  showKeyboardShortcuts: true,
  animateTransitions: true,
  density: 'comfortable',
  showProfilePictures: true,
  showStatusBadges: true,
};

const defaultPaginationPreferences: PaginationPreferences = {
  defaultPageSize: 10,
  showPageSizeSelector: true,
  showTotalCount: true,
};

const defaultTablePreferences: TablePreferences = {
  stripedRows: true,
  hoverHighlight: true,
  compactMode: false,
  showBorders: true,
  stickyHeader: true,
};

const initialState = {
  language: 'en' as Language,
  dateFormat: 'MM/DD/YYYY' as DateFormat,
  timeFormat: '12h' as TimeFormat,
  currency: 'TZS' as Currency,
  numberFormat: 'comma' as NumberFormat,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  theme: defaultThemePreferences,
  notifications: defaultNotificationPreferences,
  display: defaultDisplayPreferences,
  pagination: defaultPaginationPreferences,
  table: defaultTablePreferences,
  recentlyViewed: [],
  favorites: [],
  dashboardLayout: [],
};

// ============================================================================
// STORE
// ============================================================================

/**
 * User Preferences Store
 *
 * Persistent store for user preferences
 * Data is saved to localStorage
 */
export const useUserPreferencesStore = create<UserPreferencesState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ===== LOCALE ACTIONS =====

        setLanguage: (language: Language) => {
          set({ language }, false, 'setLanguage');
          // Sync with i18n translation system
          if (i18n.language !== language) {
            i18n.changeLanguage(language);
          }
        },

        setDateFormat: (format: DateFormat) => {
          set({ dateFormat: format }, false, 'setDateFormat');
        },

        setTimeFormat: (format: TimeFormat) => {
          set({ timeFormat: format }, false, 'setTimeFormat');
        },

        setCurrency: (currency: Currency) => {
          set({ currency }, false, 'setCurrency');
        },

        setNumberFormat: (format: NumberFormat) => {
          set({ numberFormat: format }, false, 'setNumberFormat');
        },

        setTimezone: (timezone: string) => {
          set({ timezone }, false, 'setTimezone');
        },

        // ===== THEME PREFERENCES =====

        setThemePreferences: (prefs: Partial<ThemePreferences>) => {
          set(
            (state) => ({
              theme: { ...state.theme, ...prefs },
            }),
            false,
            'setThemePreferences'
          );
        },

        setColorPreset: (presetId: string) => {
          set(
            (state) => ({
              theme: { ...state.theme, colorPresetId: presetId },
            }),
            false,
            'setColorPreset'
          );
        },

        setThemeMode: (mode: ThemeMode) => {
          set(
            (state) => ({
              theme: { ...state.theme, mode },
            }),
            false,
            'setThemeMode'
          );
        },

        setFontFamily: (fontId: string) => {
          set(
            (state) => ({
              theme: { ...state.theme, fontFamilyId: fontId },
            }),
            false,
            'setFontFamily'
          );
        },

        // ===== NOTIFICATION PREFERENCES =====

        setNotificationPreferences: (prefs: Partial<NotificationPreferences>) => {
          set(
            (state) => ({
              notifications: { ...state.notifications, ...prefs },
            }),
            false,
            'setNotificationPreferences'
          );
        },

        toggleNotification: (key: keyof NotificationPreferences) => {
          set(
            (state) => ({
              notifications: {
                ...state.notifications,
                [key]: !state.notifications[key],
              },
            }),
            false,
            'toggleNotification'
          );
        },

        // ===== DISPLAY PREFERENCES =====

        setDisplayPreferences: (prefs: Partial<DisplayPreferences>) => {
          set(
            (state) => ({
              display: { ...state.display, ...prefs },
            }),
            false,
            'setDisplayPreferences'
          );
        },

        setDensity: (density: Density) => {
          set(
            (state) => ({
              display: { ...state.display, density },
            }),
            false,
            'setDensity'
          );
        },

        // ===== PAGINATION PREFERENCES =====

        setPaginationPreferences: (prefs: Partial<PaginationPreferences>) => {
          set(
            (state) => ({
              pagination: { ...state.pagination, ...prefs },
            }),
            false,
            'setPaginationPreferences'
          );
        },

        setDefaultPageSize: (size: number) => {
          set(
            (state) => ({
              pagination: { ...state.pagination, defaultPageSize: size },
            }),
            false,
            'setDefaultPageSize'
          );
        },

        // ===== TABLE PREFERENCES =====

        setTablePreferences: (prefs: Partial<TablePreferences>) => {
          set(
            (state) => ({
              table: { ...state.table, ...prefs },
            }),
            false,
            'setTablePreferences'
          );
        },

        // ===== RECENTLY VIEWED =====

        addRecentlyViewed: (item: {
          type: 'policy' | 'claim' | 'customer';
          id: string;
          title: string;
        }) => {
          set(
            (state) => {
              // Remove if already exists
              const filtered = state.recentlyViewed.filter(
                (rv) => !(rv.type === item.type && rv.id === item.id)
              );

              // Add to beginning
              const updated = [{ ...item, timestamp: Date.now() }, ...filtered];

              // Keep only last 10
              return { recentlyViewed: updated.slice(0, 10) };
            },
            false,
            'addRecentlyViewed'
          );
        },

        clearRecentlyViewed: () => {
          set({ recentlyViewed: [] }, false, 'clearRecentlyViewed');
        },

        // ===== FAVORITES =====

        addFavorite: (item: {
          type: 'policy' | 'claim' | 'customer' | 'report';
          id: string;
          title: string;
        }) => {
          set(
            (state) => {
              // Don't add if already exists
              const exists = state.favorites.some(
                (fav) => fav.type === item.type && fav.id === item.id
              );

              if (exists) return state;

              return {
                favorites: [...state.favorites, { ...item, timestamp: Date.now() }],
              };
            },
            false,
            'addFavorite'
          );
        },

        removeFavorite: (type: string, id: string) => {
          set(
            (state) => ({
              favorites: state.favorites.filter((fav) => !(fav.type === type && fav.id === id)),
            }),
            false,
            'removeFavorite'
          );
        },

        isFavorite: (type: string, id: string): boolean => {
          return get().favorites.some((fav) => fav.type === type && fav.id === id);
        },

        clearFavorites: () => {
          set({ favorites: [] }, false, 'clearFavorites');
        },

        // ===== DASHBOARD LAYOUT =====

        setDashboardLayout: (layout) => {
          set({ dashboardLayout: layout }, false, 'setDashboardLayout');
        },

        toggleWidgetVisibility: (widgetId: string) => {
          set(
            (state) => ({
              dashboardLayout: state.dashboardLayout.map((widget) =>
                widget.id === widgetId ? { ...widget, visible: !widget.visible } : widget
              ),
            }),
            false,
            'toggleWidgetVisibility'
          );
        },

        // ===== RESET =====

        reset: () => {
          set(initialState, false, 'reset');
        },
      }),
      {
        name: 'user-preferences-storage',
        // Only persist non-sensitive data
        partialize: (state) => ({
          language: state.language,
          dateFormat: state.dateFormat,
          timeFormat: state.timeFormat,
          currency: state.currency,
          numberFormat: state.numberFormat,
          timezone: state.timezone,
          theme: state.theme,
          notifications: state.notifications,
          display: state.display,
          pagination: state.pagination,
          table: state.table,
          recentlyViewed: state.recentlyViewed,
          favorites: state.favorites,
          dashboardLayout: state.dashboardLayout,
        }),
      }
    ),
    { name: 'UserPreferencesStore' }
  )
);
